import { useEffect, useState, type ComponentType } from "react";
import { Link, Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  Activity,
  Award,
  BadgeCheck,
  BarChart3,
  Bell,
  BookOpenCheck,
  Building,
  ClipboardList,
  Cog,
  Database,
  FileClock,
  FileSearch,
  FolderKanban,
  Gauge,
  Globe2,
  HandHeart,
  Home,
  Inbox,
  KeyRound,
  Landmark,
  LogOut,
  Mail,
  Map,
  Menu,
  MessageSquareQuote,
  Network,
  ScrollText,
  ShieldCheck,
  SlidersHorizontal,
  UserCog,
  Users,
  X,
} from "lucide-react";
import { useSession, type Access, type Me } from "../lib/session";
import { Wordmark } from "./Logo";
import { Badge, Loading, SyntheticBanner, cx } from "./ui";
import { useLang } from "../lib/i18n";
import { LangToggle } from "./LangToggle";

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  show?: boolean;
  badge?: number;
}
interface NavSection {
  title?: string;
  items: NavItem[];
}

function navFor(user: Me, access: Access, can: (p: string) => boolean, tx: (en: string, hi: string) => string): NavSection[] {
  const personal: NavSection = {
    title: tx("You", "आप"),
    items: [
      { to: "/account/inbox", label: tx("Inbox", "इनबॉक्स"), icon: Inbox, badge: user.unreadNotifications },
      { to: "/account/certificate", label: tx("Rashtramitra certificate", "राष्ट्रमित्र प्रमाण पत्र"), icon: Award },
      { to: "/account/profile", label: tx("Profile & security", "प्रोफ़ाइल और सुरक्षा"), icon: UserCog },
    ],
  };
  switch (user.workspace) {
    case "citizen":
      return [
        {
          items: [
            { to: "/account", label: tx("My area", "मेरा इलाक़ा"), icon: Home },
            { to: "/account/complaints", label: tx("My complaints", "मेरी शिकायतें"), icon: ClipboardList },
            { to: "/account/promises", label: tx("My promises & votes", "मेरे वादे और राय"), icon: ScrollText },
            { to: "/account/participate", label: tx("Volunteer & Fellowship", "वॉलंटियर और फ़ेलोशिप"), icon: HandHeart },
          ],
        },
        personal,
      ];
    case "volunteer":
      return [{ items: [{ to: "/workspace", label: tx("My tasks", "मेरे काम"), icon: FolderKanban }, { to: "/account", label: tx("My area", "मेरा इलाक़ा"), icon: Home }] }, personal];
    case "fellow":
      return [
        {
          title: tx("Civic leadership", "नागरिक नेतृत्व"),
          items: [
            { to: "/workspace", label: tx("Overview", "सारांश"), icon: Gauge },
            { to: "/workspace/cases", label: tx("Verification queue", "जाँच की कतार"), icon: ClipboardList },
            { to: "/workspace/tasks", label: tx("Volunteer tasks", "वॉलंटियर के काम"), icon: FolderKanban },
            { to: "/workspace/access", label: tx("Access requests", "इजाज़त की अर्ज़ियाँ"), icon: KeyRound },
            { to: "/workspace/analytics", label: tx("Patterns", "पैटर्न"), icon: BarChart3, show: can("analytics.view") },
          ],
        },
        personal,
      ];
    case "representative":
      return [
        {
          title: access.representative ? tx("Your office", "आपका दफ़्तर") : tx("Delegated office", "सौंपा गया दफ़्तर"),
          items: [
            { to: "/workspace", label: tx("Constituency overview", "चुनाव क्षेत्र का सारांश"), icon: Gauge },
            { to: "/workspace/cases", label: tx("Constituency complaints", "चुनाव क्षेत्र की शिकायतें"), icon: ClipboardList },
            { to: "/workspace/promises", label: tx("Promises", "वादे"), icon: ScrollText },
            { to: "/workspace/associates", label: tx("Associates", "सहयोगी"), icon: Users, show: user.role === "elected_representative" },
            { to: "/workspace/access", label: tx("Access requests", "इजाज़त की अर्ज़ियाँ"), icon: KeyRound },
          ],
        },
        personal,
      ];
    case "party":
      return [{ title: tx("Party", "पार्टी"), items: [{ to: "/workspace", label: tx("Party overview", "पार्टी का सारांश"), icon: Landmark }, { to: "/workspace/cases", label: tx("Constituency activity", "चुनाव क्षेत्र की गतिविधि"), icon: ClipboardList }] }, personal];
    case "organisation":
      return [{ title: tx("Organisation", "संस्था"), items: [{ to: "/workspace", label: tx("Organisation", "संस्था"), icon: Building }, { to: "/workspace/cases", label: tx("Shared cases", "साझा मामले"), icon: ClipboardList }] }, personal];
    case "auditor":
      return [
        {
          title: tx("Oversight", "निगरानी"),
          items: [
            { to: "/workspace", label: tx("Oversight", "निगरानी"), icon: Gauge },
            { to: "/workspace/analytics", label: tx("Analytics", "विश्लेषण"), icon: BarChart3 },
            { to: "/workspace/cases", label: tx("Case records", "मामलों के रिकॉर्ड"), icon: ClipboardList, show: can("complaints.view") },
            { to: "/workspace/admin/audit", label: tx("Audit log", "ऑडिट लॉग"), icon: FileClock },
            { to: "/workspace/access", label: tx("Grants & requests", "इजाज़तें और अर्ज़ियाँ"), icon: KeyRound },
            { to: "/workspace/dockets", label: tx("Collective dockets", "साझा डॉकेट"), icon: Network },
            { to: "/workspace/admin/data", label: tx("Data stewardship", "डेटा की देखरेख"), icon: Database },
          ],
        },
        personal,
      ];
    case "investigation":
      return [{ title: tx("Investigation", "जाँच"), items: [{ to: "/workspace", label: tx("Granted cases", "मिले हुए मामले"), icon: FileSearch }, { to: "/workspace/access", label: tx("Grants", "इजाज़तें"), icon: KeyRound }] }, personal];
    default:
      return [
        {
          title: tx("Control", "कंट्रोल"),
          items: [
            { to: "/workspace", label: tx("Command center", "कमांड सेंटर"), icon: Gauge },
            { to: "/workspace/admin/audit", label: tx("Audit log", "ऑडिट लॉग"), icon: FileClock, show: can("audit.view") },
            { to: "/workspace/analytics", label: tx("Analytics", "विश्लेषण"), icon: BarChart3, show: can("analytics.view") },
          ],
        },
        {
          title: tx("Civic operations", "नागरिक कामकाज"),
          items: [
            { to: "/workspace/cases", label: tx("Complaints & cases", "शिकायतें और मामले"), icon: ClipboardList, show: can("complaints.view") },
            { to: "/workspace/promises", label: tx("Promises", "वादे"), icon: ScrollText, show: can("promises.view") },
            { to: "/workspace/admin/applications", label: tx("Applications", "आवेदन"), icon: BookOpenCheck, show: can("applications.view") },
            { to: "/workspace/tasks", label: tx("Volunteer tasks", "वॉलंटियर के काम"), icon: FolderKanban, show: can("volunteers.assign_tasks") },
            { to: "/workspace/access", label: tx("Access & grants", "पहुँच और इजाज़तें"), icon: KeyRound, show: can("access_requests.decide") || can("grants.manage") },
            { to: "/workspace/dockets", label: tx("Dockets & patterns", "डॉकेट और पैटर्न"), icon: Network, show: can("dockets.manage") },
            { to: "/workspace/admin/moderation", label: tx("Moderation", "मॉडरेशन"), icon: MessageSquareQuote, show: can("participation.moderate") },
          ],
        },
        {
          title: tx("Identity & governance", "पहचान और शासन"),
          items: [
            { to: "/workspace/admin/users", label: tx("Users & roles", "यूज़र और भूमिकाएँ"), icon: Users, show: can("users.view") },
            { to: "/workspace/admin/governance", label: tx("Representatives & orgs", "जनप्रतिनिधि और संस्थाएँ"), icon: Landmark, show: can("representatives.view") || can("organisations.manage") || can("geography.manage") },
            { to: "/workspace/admin/claims", label: tx("Profile claims", "प्रोफ़ाइल दावे"), icon: BadgeCheck, show: can("representatives.manage") },
            { to: "/workspace/admin/role-permissions", label: tx("Permissions by role", "भूमिका के हिसाब से अनुमतियाँ"), icon: SlidersHorizontal, show: user?.role === "super_admin" },
            { to: "/workspace/admin/geography", label: tx("Geography", "भूगोल (इलाक़े)"), icon: Map, show: can("geography.manage") || can("geography.resolve_flags") },
          ],
        },
        {
          title: tx("Data & system", "डेटा और सिस्टम"),
          items: [
            { to: "/workspace/admin/data", label: tx("Data stewardship", "डेटा की देखरेख"), icon: Database, show: can("data.export") || can("data.retention_manage") },
            { to: "/workspace/admin/services", label: tx("Commercial services", "व्यावसायिक सेवाएँ"), icon: Globe2, show: can("services.manage_clients") || can("services.view_usage") },
            { to: "/workspace/admin/communications", label: tx("Communications", "संदेश और ईमेल"), icon: Mail, show: can("notifications.view_outbox") },
            { to: "/workspace/admin/settings", label: tx("Configuration", "सेटिंग्स"), icon: Cog, show: can("system.view_status") },
          ],
        },
        personal,
      ];
  }
}

export function WorkspaceLayout() {
  const { user, access, mfa, loading, can, signOut, config } = useSession();
  const loc = useLocation();
  const { tx, tl } = useLang();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  if (loading) return <Loading label={tx("Opening your workspace…", "आपका खाता खुल रहा है…")} />;
  if (!user || !access) return <Navigate to={`/signin?returnTo=${encodeURIComponent(loc.pathname)}`} replace />;
  if (mfa && !mfa.satisfied) return <Navigate to={`/security?returnTo=${encodeURIComponent(loc.pathname)}`} replace />;
  if (loc.pathname.startsWith("/workspace") && user.workspace === "citizen") return <Navigate to="/account" replace />;

  const sections = navFor(user, access, can, tx).map((s) => ({ ...s, items: s.items.filter((i) => i.show !== false) })).filter((s) => s.items.length);

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center px-5">
        <Link to="/" aria-label={tx("Seva Sangrah home", "सेवा संग्रह होम")}>
          <Wordmark />
        </Link>
      </div>
      <div className="px-4 pb-3">
        <LangToggle />
      </div>
      <div className="px-4 pb-3">
        <div className="rounded-2xl bg-ink-50 p-3">
          <div className="truncate text-[13.5px] font-semibold text-ink-900">{user.fullName}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[12px] text-ink-500">
            {tl(user.roleLabel)}
            {user.isSynthetic && <Badge tone="saffron">{tx("Demo", "डेमो")}</Badge>}
          </div>
          {access.modules.length > 0 && (
            <div className="mt-1.5 text-[11.5px] text-ink-400">
              {tx("Modules", "मॉड्यूल")}: {access.modules.map((m) => m.label).join(", ")}
            </div>
          )}
          {!access.geography.global && user.workspace !== "citizen" && (access.geography.states.length + access.geography.districts.length > 0) && (
            <div className="mt-1 truncate text-[11.5px] text-ink-400">
              {tx("Scope", "दायरा")}: {[...access.geography.states.map((x) => tl(x)), ...access.geography.districts.map((d) => d.split("|")[1])].join(", ")}
            </div>
          )}
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 pb-6" aria-label={tx("Workspace", "वर्कस्पेस")}>
        {sections.map((s, i) => (
          <div key={i} className="mt-4">
            {s.title && <div className="eyebrow px-3 pb-1.5 text-[10.5px] text-ink-400">{s.title}</div>}
            <ul className="space-y-0.5">
              {s.items.map((it) => (
                <li key={it.to}>
                  <NavLink
                    end={it.to === "/workspace" || it.to === "/account"}
                    to={it.to}
                    className={({ isActive }) => cx("flex items-center gap-3 rounded-xl px-3 py-2 text-[13.5px] font-medium transition-colors", isActive ? "bg-ink-900 text-white" : "text-ink-600 hover:bg-ink-50 hover:text-ink-900")}
                  >
                    <it.icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1 truncate">{it.label}</span>
                    {!!it.badge && <span className="rounded-full bg-saffron-500 px-1.5 text-[11px] font-semibold text-ink-950">{it.badge}</span>}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      <div className="border-t hairline p-3">
        {user.workspace !== "citizen" && (
          <Link to="/account" className="flex items-center gap-3 rounded-xl px-3 py-2 text-[13px] text-ink-500 hover:bg-ink-50">
            <Activity className="h-4 w-4" /> {tx("Citizen view", "नागरिक वाला व्यू")}
          </Link>
        )}
        <button onClick={signOut} className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-[13px] text-ink-500 hover:bg-ink-50 cursor-pointer">
          <LogOut className="h-4 w-4" /> {tx("Sign out", "साइन आउट")}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-paper">
      {config && config.environment !== "production" && <SyntheticBanner />}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r hairline bg-white lg:block" style={{ top: config && config.environment !== "production" ? 30 : 0 }}>
        {sidebar}
      </aside>
      <div className="sticky top-0 z-30 flex h-14 items-center justify-between border-b hairline bg-white/90 px-4 backdrop-blur lg:hidden">
        <button onClick={() => setOpen(true)} aria-label={tx("Open navigation", "मेन्यू खोलें")} className="rounded-full p-2 cursor-pointer">
          <Menu className="h-5 w-5" />
        </button>
        <Wordmark />
        <div className="flex items-center gap-1">
          <LangToggle />
          <Link to="/account/inbox" className="relative rounded-full p-2" aria-label={tx("Inbox", "इनबॉक्स")}>
            <Bell className="h-5 w-5" />
            {user.unreadNotifications > 0 && <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-saffron-500" />}
          </Link>
        </div>
      </div>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-ink-950/40" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 bg-white shadow-2xl">
            <button onClick={() => setOpen(false)} className="absolute right-3 top-4 rounded-full p-1.5 cursor-pointer" aria-label={tx("Close navigation", "मेन्यू बंद करें")}>
              <X className="h-5 w-5" />
            </button>
            {sidebar}
          </div>
        </div>
      )}
      <main className="lg:pl-64">
        <div className="mx-auto max-w-6xl px-5 py-8 sm:px-8 sm:py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export function RequireUser({ children }: { children: React.ReactNode }) {
  const { user, loading } = useSession();
  const loc = useLocation();
  if (loading) return <Loading />;
  if (!user) return <Navigate to={`/signin?returnTo=${encodeURIComponent(loc.pathname)}`} replace />;
  return <>{children}</>;
}

export { ShieldCheck };
